import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { getHotelDateFormat } from '../../utils/dateUtils';
import {
  fetchFinanceDraftInvoiceDefaults,
  getRateForDate,
} from '../../services/api';
import { detectThemeSync } from '../../utils/themeUtils';
import useMobileView from '../../hooks/useMobileView';

const DISPLAY_DATE_FORMATS = ['DD MMM YYYY', 'D MMM YYYY', 'YYYY-MM-DD'];

function parseDisplayDate(value) {
  if (!value) return null;
  const parsed = moment(value, DISPLAY_DATE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
}

function formatDisplayDate(value) {
  if (!value) return '';
  const parsed = moment.isMoment(value) ? value : parseDisplayDate(value);
  return parsed?.isValid() ? parsed.format('DD MMM YYYY') : '';
}

const TIME_PARSE_FORMATS = ['HH:mm', 'HH:mm:ss', 'H:mm', 'h:mm A', 'hh:mm A'];

function parseDisplayTime(value) {
  if (!value) return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;
  const parsed = moment(String(value).trim(), TIME_PARSE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
}

function formatTimeForApi(value) {
  const parsed = parseDisplayTime(value);
  return parsed?.isValid() ? parsed.format('HH:mm') : '';
}

function toApiDate(value) {
  const parsed = parseDisplayDate(value);
  return parsed?.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

function computeNightsFromDates(checkIn, checkOut) {
  const start = parseDisplayDate(checkIn);
  const end = parseDisplayDate(checkOut);
  if (!start?.isValid() || !end?.isValid()) return null;
  return Math.max(0, end.diff(start, 'days'));
}

function effectiveNights(nights) {
  return Math.max(1, parseInt(nights, 10) || 1);
}

function effectiveRooms(roomCount) {
  return Math.max(1, parseInt(roomCount, 10) || 1);
}

function parseNum(value) {
  const n = parseFloat(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(value) {
  return parseNum(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function computeRatePerNight(amount, roomCount, nights) {
  const n = effectiveNights(nights);
  const rooms = effectiveRooms(roomCount);
  const total = parseNum(amount);
  if (!total) return 0;
  return Math.round((total / n / rooms) * 100) / 100;
}

function computeLineAmount(ratePerNight, roomCount, nights) {
  const n = effectiveNights(nights);
  const rooms = effectiveRooms(roomCount);
  return Math.round(parseNum(ratePerNight) * n * rooms * 100) / 100;
}

function normalizeLineItem(row, nights) {
  const autoCalculate = row.auto_calculate !== false;
  const rate = row.rate_per_night != null && row.rate_per_night !== ''
    ? parseNum(row.rate_per_night)
    : computeRatePerNight(row.amount, row.room_count, nights);
  return {
    ...row,
    auto_calculate: autoCalculate,
    rate_per_night: rate,
    amount: autoCalculate ? computeLineAmount(rate, row.room_count, nights) : parseNum(row.amount),
  };
}

function recalculateLineItemsForNights(lineItems, nights) {
  return (lineItems || []).map((row) => normalizeLineItem(row, nights));
}

function applyStayDatePatch(draft, patch) {
  const next = { ...draft, ...patch };
  if ('check_in' in patch || 'check_out' in patch) {
    const nights = computeNightsFromDates(next.check_in, next.check_out);
    if (nights != null) next.nights = nights;
  }
  if ('check_in' in patch || 'check_out' in patch || 'nights' in patch) {
    next.line_items = recalculateLineItemsForNights(next.line_items || [], next.nights);
  }
  return next;
}

function computeTotals(draft, useFixedDiscount) {
  const lineTotal = (draft.line_items || []).reduce((sum, row) => sum + parseNum(row.amount), 0);
  let discount = 0;
  if (useFixedDiscount) {
    discount = parseNum(draft.discount_amount);
  } else {
    discount = (lineTotal * parseNum(draft.discount_percent)) / 100;
  }
  discount = Math.min(discount, lineTotal);
  const subtotal = Math.max(0, lineTotal - discount);
  const gst = draft.gst_included ? (subtotal * parseNum(draft.gst_percent)) / 100 : 0;
  const grandTotal = subtotal + gst;
  const paid = parseNum(draft.paid_amount);
  const remaining = Math.max(0, grandTotal - paid);
  return { lineTotal, discount, subtotal, gst, grandTotal, paid, remaining };
}

export function buildFinanceDraftInvoicePayload(draft, useFixedDiscount) {
  if (!draft) return null;
  return {
    invoice_scope: draft.invoice_scope || 'hotel',
    line_items: (draft.line_items || []).map((row) => ({
      description: row.description || '',
      room_type: row.room_type || '',
      rate_plan: row.rate_plan || '',
      room_count: Math.max(1, parseInt(row.room_count, 10) || 1),
      amount: parseNum(row.amount),
    })),
    discount_percent: useFixedDiscount ? 0 : parseNum(draft.discount_percent),
    discount_amount: useFixedDiscount ? parseNum(draft.discount_amount) : null,
    gst_percent: parseNum(draft.gst_percent),
    gst_included: Boolean(draft.gst_included),
    paid_amount: draft.show_paid_remaining ? parseNum(draft.paid_amount) : null,
    show_paid_remaining: Boolean(draft.show_paid_remaining),
    guest_name: draft.guest_name || '',
    guest_address: draft.guest_address || '',
    show_corporate_billing: Boolean(draft.show_corporate_billing),
    company_name: draft.company_name || '',
    company_gstin: draft.company_gstin || '',
    company_phone: draft.company_phone || '',
    company_email: draft.company_email || '',
    company_address: draft.company_address || '',
    invoice_number: draft.invoice_number || '',
    invoice_date: draft.invoice_date || '',
    booking_code: draft.booking_code || '',
    check_in: draft.check_in || '',
    check_out: draft.check_out || '',
    check_in_time: formatTimeForApi(draft.check_in_time),
    check_out_time: formatTimeForApi(draft.check_out_time),
    nights: draft.nights != null ? parseInt(draft.nights, 10) : null,
  };
}

const emptyLineItem = (roomTypes = []) => {
  const first = roomTypes[0];
  return {
    description: '',
    room_type: first?.name || 'Room',
    room_type_id: first?.id ?? null,
    rate_plan: first?.rate_plan || '',
    rate_plan_id: first?.rate_plan_id ?? null,
    room_count: 1,
    rate_per_night: first?.rate_per_night ?? 0,
    amount: first?.rate_per_night ?? 0,
    auto_calculate: true,
  };
};

function fieldSx(isDark, accentColor) {
  return {
    '& .MuiInputBase-root': {
      borderRadius: '10px',
      bgcolor: isDark ? alpha('#ffffff', 0.04) : '#ffffff',
      color: isDark ? '#f8fafc' : '#0f172a',
      fontSize: '0.875rem',
    },
    '& .MuiInputLabel-root': {
      color: isDark ? '#94a3b8' : '#64748b',
      fontSize: '0.8rem',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? alpha('#ffffff', 0.12) : alpha('#101828', 0.14),
    },
    '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(accentColor, 0.45),
    },
    '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: accentColor,
    },
  };
}

function sectionCardSx(isDark, accentColor) {
  return {
    p: { xs: 1.25, sm: 1.5 },
    borderRadius: '14px',
    border: `1px solid ${alpha(accentColor, isDark ? 0.22 : 0.2)}`,
    bgcolor: isDark ? alpha('#ffffff', 0.03) : alpha('#ffffff', 0.95),
    boxShadow: isDark ? 'none' : '0 8px 24px rgba(15,23,42,0.04)',
  };
}

function hasCorporateBillingData(draft) {
  if (!draft) return false;
  return Boolean(
    draft.company_name
    || draft.company_gstin
    || draft.company_phone
    || draft.company_email
    || draft.company_address,
  );
}

export default function FinanceDraftInvoiceEditor({
  accentColor = '#4ce1b6',
  onDraftChange,
  onGenerateDraft,
  generating = false,
}) {
  const { isCompact } = useMobileView();
  const isDark = detectThemeSync() === 'dark';
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [defaultsError, setDefaultsError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [useFixedDiscount, setUseFixedDiscount] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const loadDefaults = useCallback(async (checkInDate = null) => {
    setLoadingDefaults(true);
    setDefaultsError(null);
    try {
      const data = await fetchFinanceDraftInvoiceDefaults(checkInDate);
      const nights = effectiveNights(
        data.nights ?? computeNightsFromDates(data.check_in, data.check_out) ?? 1,
      );
      const types = data.room_types || [];
      const plans = data.rate_plans || [];
      setRoomTypes(types);
      setRatePlans(plans);
      const nextDraft = {
        ...data,
        nights,
        line_items: recalculateLineItemsForNights(
          (data.line_items || []).map((row) => ({ ...row })),
          nights,
        ),
        invoice_scope: data.invoice_scope || 'hotel',
      };
      const nextUseFixedDiscount = data.discount_amount != null && parseNum(data.discount_amount) > 0;
      setUseFixedDiscount(nextUseFixedDiscount);
      setDraft(nextDraft);
      onDraftChange?.(nextDraft);
    } catch (err) {
      setDefaultsError(err?.response?.data?.error || err?.message || 'Failed to load draft defaults.');
    } finally {
      setLoadingDefaults(false);
    }
  }, [onDraftChange]);

  useEffect(() => {
    loadDefaults();
  }, [loadDefaults]);

  useEffect(() => {
    if (!draft || !onGenerateDraft || loadingDefaults) return undefined;
    const payload = buildFinanceDraftInvoicePayload(draft, useFixedDiscount);
    if (!payload) return undefined;
    const timer = window.setTimeout(() => {
      onGenerateDraft(payload);
    }, 450);
    return () => window.clearTimeout(timer);
  }, [draft, loadingDefaults, onGenerateDraft, useFixedDiscount]);

  const totals = useMemo(
    () => (draft ? computeTotals(draft, useFixedDiscount) : null),
    [draft, useFixedDiscount],
  );

  const updateDraft = (patch) => {
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      onDraftChange?.(next);
      return next;
    });
  };

  const fetchLineRate = async (roomTypeId, ratePlanId, checkIn) => {
    const apiDate = toApiDate(checkIn) || moment().format('YYYY-MM-DD');
    if (!roomTypeId) return 0;
    try {
      const data = await getRateForDate(apiDate, roomTypeId, ratePlanId || undefined);
      return parseNum(data?.final_rate ?? data?.base_rate ?? 0);
    } catch {
      const match = roomTypes.find((rt) => rt.id === roomTypeId);
      return parseNum(match?.rate_per_night ?? 0);
    }
  };

  const updateLineItem = async (index, patch) => {
    const nights = effectiveNights(draft?.nights);
    let rowPatch = { ...patch };

    if ('room_type_id' in patch) {
      const selected = roomTypes.find((rt) => rt.id === patch.room_type_id);
      if (selected) {
        rowPatch = {
          ...rowPatch,
          room_type: selected.name,
          rate_plan_id: patch.rate_plan_id ?? selected.rate_plan_id,
          rate_plan: ratePlans.find((p) => p.id === (patch.rate_plan_id ?? selected.rate_plan_id))?.name
            || selected.rate_plan
            || '',
        };
        const rate = await fetchLineRate(
          selected.id,
          rowPatch.rate_plan_id,
          draft?.check_in,
        );
        rowPatch.rate_per_night = rate;
      }
    }

    if ('rate_plan_id' in patch && !('room_type_id' in patch)) {
      const plan = ratePlans.find((p) => p.id === patch.rate_plan_id);
      rowPatch.rate_plan = plan?.name || '';
      const currentRow = draft.line_items[index];
      const rate = await fetchLineRate(
        currentRow.room_type_id,
        patch.rate_plan_id,
        draft?.check_in,
      );
      rowPatch.rate_per_night = rate;
    }

    setDraft((prev) => {
      const lineItems = [...(prev.line_items || [])];
      let row = { ...lineItems[index], ...rowPatch };
      if ('amount' in rowPatch && !('rate_per_night' in rowPatch)) {
        row.rate_per_night = computeRatePerNight(row.amount, row.room_count, nights);
      } else {
        row = normalizeLineItem(row, nights);
      }
      lineItems[index] = row;
      const next = { ...prev, line_items: lineItems };
      onDraftChange?.(next);
      return next;
    });
  };

  const updateStayDates = async (patch) => {
    setDraft((prev) => {
      const next = applyStayDatePatch(prev, patch);
      onDraftChange?.(next);
      return next;
    });
    if ('check_in' in patch) {
      const apiDate = toApiDate(patch.check_in ?? draft?.check_in);
      if (apiDate) {
        const data = await fetchFinanceDraftInvoiceDefaults(apiDate);
        setRoomTypes(data.room_types || []);
        setRatePlans(data.rate_plans || []);
        setDraft((prev) => {
          const nights = effectiveNights(prev.nights);
          const lineItems = (prev.line_items || []).map((row, idx) => {
            const meta = (data.room_types || []).find((rt) => rt.id === row.room_type_id);
            const rate = meta?.rate_per_night ?? row.rate_per_night;
            return normalizeLineItem({ ...row, rate_per_night: rate }, nights);
          });
          const next = { ...prev, line_items: lineItems };
          onDraftChange?.(next);
          return next;
        });
      }
    }
  };

  const addAllRoomTypes = () => {
    if (!roomTypes.length) return;
    const nights = effectiveNights(draft?.nights);
    const newItems = roomTypes.map((rt) => normalizeLineItem({
      description: '',
      room_type: rt.name,
      room_type_id: rt.id,
      rate_plan: rt.rate_plan || '',
      rate_plan_id: rt.rate_plan_id,
      room_count: 1,
      rate_per_night: rt.rate_per_night ?? 0,
      amount: 0,
      auto_calculate: true,
    }, nights));
    setDraft((prev) => {
      const next = { ...prev, line_items: newItems };
      onDraftChange?.(next);
      return next;
    });
  };

  const addLineItem = () => {
    setDraft((prev) => {
      const nights = effectiveNights(prev.nights);
      const next = {
        ...prev,
        line_items: [...(prev.line_items || []), normalizeLineItem(emptyLineItem(roomTypes), nights)],
      };
      onDraftChange?.(next);
      return next;
    });
  };

  const removeLineItem = (index) => {
    setDraft((prev) => {
      const lineItems = [...(prev.line_items || [])];
      lineItems.splice(index, 1);
      const next = {
        ...prev,
        line_items: lineItems.length ? lineItems : [emptyLineItem(roomTypes)],
      };
      onDraftChange?.(next);
      return next;
    });
  };

  const inputSx = fieldSx(isDark, accentColor);
  const datePickerSx = {
    ...inputSx,
    '& .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? alpha('#ffffff', 0.12) : alpha('#101828', 0.14),
    },
  };

  if (loadingDefaults && !draft) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
          Loading smart draft editor…
        </Typography>
      </Box>
    );
  }

  if (defaultsError && !draft) {
    return (
      <Stack spacing={1.5}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{defaultsError}</Alert>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadDefaults()}>
          Retry
        </Button>
      </Stack>
    );
  }

  if (!draft) return null;

  const currency = draft.currency || 'INR';

  return (
    <Stack spacing={isCompact ? 1.5 : 2}>
      <Alert
        severity="info"
        icon={<AutoAwesomeIcon fontSize="inherit" />}
        sx={{
          borderRadius: 2,
          bgcolor: isDark ? alpha(accentColor, 0.1) : alpha(accentColor, 0.08),
          color: isDark ? '#f8fafc' : '#0f172a',
          '& .MuiAlert-icon': { color: accentColor },
        }}
      >
        Build a custom invoice with live rates — nothing is saved to bookings or payments.
      </Alert>

      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Stay dates</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.75 }}>
        Booking ID and stay dates appear on the invoice. Nothing here is saved to bookings.
      </Typography>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Stack spacing={1}>
          <TextField
            size="small"
            fullWidth
            label="Booking ID (optional)"
            placeholder="e.g. BK-20260621"
            value={draft.booking_code || ''}
            onChange={(e) => updateDraft({ booking_code: e.target.value })}
            sx={inputSx}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <DatePicker
              label="Check-in"
              format={getHotelDateFormat()}
              value={parseDisplayDate(draft.check_in)}
              onChange={(value) => updateStayDates({ check_in: formatDisplayDate(value) })}
              slotProps={{ textField: { size: 'small', fullWidth: true, sx: datePickerSx } }}
            />
            <DatePicker
              label="Check-out"
              format={getHotelDateFormat()}
              value={parseDisplayDate(draft.check_out)}
              minDate={parseDisplayDate(draft.check_in) || undefined}
              onChange={(value) => updateStayDates({ check_out: formatDisplayDate(value) })}
              slotProps={{ textField: { size: 'small', fullWidth: true, sx: datePickerSx } }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <TimePicker
              label="Check-in time"
              value={parseDisplayTime(draft.check_in_time)}
              onChange={(value) => updateStayDates({ check_in_time: value?.isValid() ? value.format('HH:mm') : '' })}
              slotProps={{ textField: { size: 'small', fullWidth: true, sx: datePickerSx } }}
            />
            <TimePicker
              label="Check-out time"
              value={parseDisplayTime(draft.check_out_time)}
              onChange={(value) => updateStayDates({ check_out_time: value?.isValid() ? value.format('HH:mm') : '' })}
              slotProps={{ textField: { size: 'small', fullWidth: true, sx: datePickerSx } }}
            />
          </Stack>
          <TextField
            size="small"
            type="number"
            fullWidth
            label="Nights"
            value={draft.nights ?? 0}
            onChange={(e) => updateStayDates({ nights: e.target.value })}
            inputProps={{ min: 1 }}
            sx={inputSx}
          />
        </Stack>
      </LocalizationProvider>

      <Divider sx={{ borderColor: alpha(accentColor, 0.16) }} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Room line items
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            startIcon={<AutoAwesomeIcon />}
            onClick={addAllRoomTypes}
            disabled={!roomTypes.length}
            sx={{ textTransform: 'none', fontWeight: 700, color: accentColor }}
          >
            Add all room types
          </Button>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addLineItem}
            sx={{ textTransform: 'none', fontWeight: 700, color: accentColor }}
          >
            Add line
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={1.25}>
        {(draft.line_items || []).map((row, index) => (
          <Box
            key={`finance-draft-line-${index}`}
            sx={{
              p: 1.25,
              borderRadius: '14px',
              border: `1px solid ${alpha(accentColor, isDark ? 0.22 : 0.2)}`,
              bgcolor: isDark ? alpha('#ffffff', 0.03) : alpha('#ffffff', 0.95),
              boxShadow: isDark ? 'none' : '0 8px 24px rgba(15,23,42,0.04)',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: isDark ? '#94a3b8' : '#64748b' }}>
                Line {index + 1}
              </Typography>
              {(draft.line_items || []).length > 1 ? (
                <IconButton size="small" onClick={() => removeLineItem(index)} aria-label="Remove line">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Stack>
            <Stack spacing={1}>
              <TextField
                size="small"
                fullWidth
                label="Description (optional)"
                placeholder="Extra night, late checkout, etc."
                value={row.description || ''}
                onChange={(e) => updateLineItem(index, { description: e.target.value })}
                sx={inputSx}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <FormControl size="small" fullWidth sx={inputSx}>
                  <InputLabel>Room type</InputLabel>
                  <Select
                    label="Room type"
                    value={row.room_type_id ?? ''}
                    onChange={(e) => updateLineItem(index, { room_type_id: e.target.value })}
                  >
                    {roomTypes.map((rt) => (
                      <MenuItem key={rt.id} value={rt.id}>
                        {rt.name}
                        {rt.rate_per_night ? ` — ₹${formatMoney(rt.rate_per_night)}/night` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth sx={inputSx}>
                  <InputLabel>Rate plan</InputLabel>
                  <Select
                    label="Rate plan"
                    value={row.rate_plan_id ?? ''}
                    onChange={(e) => updateLineItem(index, { rate_plan_id: e.target.value })}
                  >
                    {ratePlans.map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  type="number"
                  label="Rooms"
                  value={row.room_count ?? 1}
                  onChange={(e) => updateLineItem(index, { room_count: e.target.value })}
                  inputProps={{ min: 1 }}
                  sx={{ ...inputSx, flex: 1 }}
                />
                {row.auto_calculate !== false ? (
                  <TextField
                    size="small"
                    type="number"
                    label={`Rate/night (${currency})`}
                    value={row.rate_per_night ?? 0}
                    onChange={(e) => updateLineItem(index, { rate_per_night: e.target.value })}
                    inputProps={{ min: 0, step: '0.01' }}
                    sx={{ ...inputSx, flex: 2 }}
                  />
                ) : null}
                <TextField
                  size="small"
                  type="number"
                  label={`Total (${currency})`}
                  value={row.amount ?? 0}
                  onChange={(e) => updateLineItem(index, { amount: e.target.value })}
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{ readOnly: row.auto_calculate !== false }}
                  helperText={
                    row.auto_calculate !== false
                      ? `${effectiveNights(draft.nights)} night(s) × ${effectiveRooms(row.room_count)} room(s)`
                      : 'Fixed charge'
                  }
                  sx={{ ...inputSx, flex: 2 }}
                />
              </Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={row.auto_calculate !== false}
                    onChange={(e) => updateLineItem(index, { auto_calculate: e.target.checked })}
                    sx={{ color: accentColor, '&.Mui-checked': { color: accentColor } }}
                  />
                }
                label={<Typography variant="caption">Auto-calculate from rate × nights</Typography>}
              />
            </Stack>
          </Box>
        ))}
      </Stack>

      <Divider sx={{ borderColor: alpha(accentColor, 0.16) }} />

      <Box sx={sectionCardSx(isDark, accentColor)}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#042018',
              background: `linear-gradient(180deg, ${alpha(accentColor, 0.95)} 0%, ${alpha(accentColor, 0.75)} 100%)`,
            }}
          >
            <PersonOutlineIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.3 }}>
              Guest & invoice details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Customer name and address shown on the invoice header.
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1.25}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small"
              fullWidth
              label="Guest name"
              placeholder="Customer or contact name"
              value={draft.guest_name || ''}
              onChange={(e) => updateDraft({ guest_name: e.target.value })}
              sx={inputSx}
            />
            <TextField
              size="small"
              fullWidth
              label="Invoice number"
              value={draft.invoice_number || ''}
              onChange={(e) => updateDraft({ invoice_number: e.target.value })}
              sx={inputSx}
            />
          </Stack>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Guest address"
            placeholder="Street, city, state, PIN"
            value={draft.guest_address || ''}
            onChange={(e) => updateDraft({ guest_address: e.target.value })}
            sx={inputSx}
          />

          <Box
            sx={{
              mt: 0.5,
              borderRadius: '12px',
              overflow: 'hidden',
              border: `1px solid ${alpha(accentColor, isDark ? 0.2 : 0.16)}`,
              bgcolor: isDark ? alpha('#ffffff', 0.02) : alpha(accentColor, 0.04),
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => updateDraft({ show_corporate_billing: !draft.show_corporate_billing })}
              sx={{
                width: '100%',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                p: 1.25,
                textAlign: 'left',
                color: 'inherit',
                '&:hover': {
                  bgcolor: isDark ? alpha(accentColor, 0.08) : alpha(accentColor, 0.08),
                },
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: accentColor,
                    bgcolor: alpha(accentColor, isDark ? 0.14 : 0.12),
                  }}
                >
                  <BusinessCenterOutlinedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                    GST / B2B billing details
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {draft.show_corporate_billing
                      ? 'Corporate block shown on invoice (REG / B2B)'
                      : 'Click to add company name, GSTIN, and billing address'}
                  </Typography>
                </Box>
              </Stack>
              <ExpandMoreIcon
                sx={{
                  flexShrink: 0,
                  color: accentColor,
                  transform: draft.show_corporate_billing ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </Box>

            <Collapse in={Boolean(draft.show_corporate_billing)}>
              <Stack
                spacing={1.25}
                sx={{
                  px: 1.25,
                  pb: 1.25,
                  pt: 0.25,
                  borderTop: `1px dashed ${alpha(accentColor, isDark ? 0.18 : 0.14)}`,
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Company name"
                    placeholder="Registered business name"
                    value={draft.company_name || ''}
                    onChange={(e) => updateDraft({ company_name: e.target.value })}
                    sx={inputSx}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="GSTIN"
                    placeholder="22AAAAA0000A1Z5"
                    value={draft.company_gstin || ''}
                    onChange={(e) => updateDraft({ company_gstin: e.target.value.toUpperCase() })}
                    sx={inputSx}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Company phone"
                    placeholder="Billing contact number"
                    value={draft.company_phone || ''}
                    onChange={(e) => updateDraft({ company_phone: e.target.value })}
                    sx={inputSx}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    type="email"
                    label="Company email"
                    placeholder="accounts@company.com"
                    value={draft.company_email || ''}
                    onChange={(e) => updateDraft({ company_email: e.target.value })}
                    sx={inputSx}
                  />
                </Stack>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  label="Company billing address"
                  placeholder="Registered office / billing address"
                  value={draft.company_address || ''}
                  onChange={(e) => updateDraft({ company_address: e.target.value })}
                  sx={inputSx}
                />
                {hasCorporateBillingData(draft) ? (
                  <Typography variant="caption" color="text.secondary">
                    Filled fields appear in the Corporate Billing Details block on the PDF.
                  </Typography>
                ) : null}
              </Stack>
            </Collapse>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: alpha(accentColor, 0.16) }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Discount & GST</Typography>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={useFixedDiscount}
            onChange={(e) => setUseFixedDiscount(e.target.checked)}
            sx={{ color: accentColor, '&.Mui-checked': { color: accentColor } }}
          />
        }
        label="Use fixed discount amount"
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        {useFixedDiscount ? (
          <TextField
            size="small"
            type="number"
            label="Discount amount"
            value={draft.discount_amount ?? 0}
            onChange={(e) => updateDraft({ discount_amount: e.target.value })}
            sx={{ ...inputSx, flex: 1 }}
          />
        ) : (
          <TextField
            size="small"
            type="number"
            label="Discount %"
            value={draft.discount_percent ?? 0}
            onChange={(e) => updateDraft({ discount_percent: e.target.value })}
            sx={{ ...inputSx, flex: 1 }}
          />
        )}
        <TextField
          size="small"
          type="number"
          label="GST %"
          value={draft.gst_percent ?? 0}
          onChange={(e) => updateDraft({ gst_percent: e.target.value })}
          sx={{ ...inputSx, flex: 1 }}
        />
      </Stack>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={Boolean(draft.gst_included)}
            onChange={(e) => updateDraft({ gst_included: e.target.checked })}
            sx={{ color: accentColor, '&.Mui-checked': { color: accentColor } }}
          />
        }
        label="Apply GST after discount"
      />

      <Button
        size="small"
        onClick={() => setShowAdvanced((v) => !v)}
        sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700, color: accentColor }}
      >
        {showAdvanced ? 'Hide' : 'Show'} payment summary on invoice
      </Button>
      <Collapse in={showAdvanced}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={Boolean(draft.show_paid_remaining)}
              onChange={(e) => updateDraft({ show_paid_remaining: e.target.checked })}
              sx={{ color: accentColor, '&.Mui-checked': { color: accentColor } }}
            />
          }
          label="Show paid & remaining"
        />
        {draft.show_paid_remaining ? (
          <TextField
            size="small"
            type="number"
            label="Paid amount"
            value={draft.paid_amount ?? 0}
            onChange={(e) => updateDraft({ paid_amount: e.target.value })}
            sx={{ ...inputSx, maxWidth: 280 }}
          />
        ) : null}
      </Collapse>

      {totals ? (
        <Box
          sx={{
            p: 1.5,
            borderRadius: '14px',
            border: `1px solid ${alpha(accentColor, 0.28)}`,
            background: isDark
              ? `linear-gradient(135deg, ${alpha(accentColor, 0.12)} 0%, ${alpha('#ffffff', 0.03)} 100%)`
              : `linear-gradient(135deg, ${alpha(accentColor, 0.1)} 0%, #ffffff 100%)`,
          }}
        >
          <Stack spacing={0.5}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Subtotal</Typography>
              <Typography variant="body2" fontWeight={700}>{currency} {formatMoney(totals.lineTotal)}</Typography>
            </Stack>
            {totals.discount > 0 ? (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Discount</Typography>
                <Typography variant="body2" fontWeight={700}>- {formatMoney(totals.discount)}</Typography>
              </Stack>
            ) : null}
            {totals.gst > 0 ? (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">GST</Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoney(totals.gst)}</Typography>
              </Stack>
            ) : null}
            <Divider sx={{ my: 0.5, borderColor: alpha(accentColor, 0.2) }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={800}>Grand total</Typography>
              <Typography variant="h6" fontWeight={800} sx={{ color: accentColor }}>
                {currency} {formatMoney(totals.grandTotal)}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      ) : null}

      <Button
        variant="contained"
        disabled={generating}
        onClick={() => {
          const payload = buildFinanceDraftInvoicePayload(draft, useFixedDiscount);
          if (payload) onGenerateDraft?.(payload, { force: true });
        }}
        sx={{
          alignSelf: 'flex-start',
          borderRadius: '10px',
          textTransform: 'none',
          fontWeight: 800,
          color: '#042018',
          background: 'linear-gradient(180deg, #58f2ce 0%, #44e8bf 100%)',
          '&:hover': { background: 'linear-gradient(180deg, #6cf8d7 0%, #52ecc7 100%)' },
        }}
      >
        {generating ? 'Updating preview…' : 'Refresh PDF preview'}
      </Button>
    </Stack>
  );
}
